<template>
  <div class="secondary-background section">
    <div class="header">
      <h3 class="title text-primary">{{ $t('home.institutions_section.title') }}</h3>
      <h4 class="subtitle text-primary">{{ $t('home.institutions_section.subtitle') }}</h4>
    </div>
    <div class="marquee-section">
      <div class="marquee">
        <div class="marquee-content scroll-left">
          <router-link
            v-for="(uni, i) in topList"
            :key="'top-' + i"
            class="logo-card"
            :to="{ name: 'University Details', params: { id: uni.id } }"
          >
            <img :src="getUniPhoto(uni)" :alt="getUniName(uni)" />
          </router-link>
          <!-- duplicate topList for infinite scroll -->
          <router-link
            v-for="(uni, i) in topList"
            :key="'top-dup-' + i"
            class="logo-card"
            :to="{ name: 'University Details', params: { id: uni.id } }"
          >
            <img :src="getUniPhoto(uni)" :alt="getUniName(uni)" />
          </router-link>
        </div>
      </div>

      <div class="marquee">
        <div class="marquee-content scroll-right">
          <div
            v-for="(uni, i) in bottomList"
            :key="'bottom-' + i"
            class="logo-card"
            @click="
              router.push({
                name: 'University Details',
                params: { id: uni.id, slug: getSlug(uni) },
              })
            "
          >
            <img :src="getUniPhoto(uni)" />
          </div>
          <!-- duplicate bottomList for infinite scroll -->
          <div
            v-for="(uni, i) in bottomList"
            :key="'bottom-dup-' + i"
            class="logo-card"
            @click="
              router.push({
                name: 'University Details',
                params: { id: uni.id, slug: getSlug(uni) },
              })
            "
          >
            <img :src="getUniPhoto(uni)" />
          </div>
        </div>
      </div>
    </div>
    <div class="footer">
      <q-btn
        :label="$t('home.institutions_section.btn_label')"
        :to="{ name: 'Universities' }"
        rounded
        class="btn-yellow"
        no-caps
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UniversityListItem } from 'src/types/University'
import { useRouter } from 'vue-router'
import { useUniversityLocale } from 'src/composables/university/useUniversityLocale'

const props = defineProps<{
  institutions: UniversityListItem[]
}>()
const router = useRouter()

const { getUniPhoto, getSlug, getUniName } = useUniversityLocale()

const midpoint = computed(() => Math.ceil(props.institutions.length / 2))

const topList = computed(() => props.institutions.slice(0, midpoint.value))

const bottomList = computed(() => props.institutions.slice(midpoint.value))
</script>

<style scoped lang="scss">
.marquee-section {
  display: flex;
  flex-direction: column;
  gap: 30px;
  overflow: hidden;

  .marquee {
    width: 100%;
    overflow: hidden;

    .marquee-content {
      display: flex;
      gap: 25px;
      width: max-content;
    }
  }
}

.scroll-left {
  animation: scroll-left 30s linear infinite;
}

.scroll-right {
  animation: scroll-right 30s linear infinite;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }

  // scroll half width, then loop
}

@keyframes scroll-right {
  0% {
    transform: translateX(-50%);
  }

  // start offset
  100% {
    transform: translateX(0);
  }
}

.logo-card {
  width: 180px;
  height: 180px;
  background: #fff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    cursor: pointer;
  }

  img {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .logo-card {
    width: 140px;
    height: 140px;

    img {
      width: 100%;
    }
  }

  .marquee-content {
    gap: 18px;
  }
}

@media (max-width: 480px) {
  .logo-card {
    width: 120px;
    height: 120px;

    img {
      width: 100%;
    }
  }

  .marquee-content {
    gap: 14px;
  }
}

@media (max-width: 420px) {
  .logo-card {
    width: 100px;
    height: 100px;

    img {
      width: 100%;
    }
  }

  .marquee-content {
    gap: 12px;
  }
}
</style>
